import type { ApiResponse, ApiErrorCode, FieldErrors } from '@talkvibe/shared';

/**
 * The single place the app talks to the API.
 *
 * Responsibilities kept here rather than in components: base URL, credentials,
 * the CSRF header, JSON encoding, and turning the `{ success, error }`
 * envelope into either a value or a thrown ApiError.
 */

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8787').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    readonly code: ApiErrorCode | 'NETWORK_ERROR',
    message: string,
    readonly status: number,
    readonly fields?: FieldErrors,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the user simply is not signed in (or no longer is). */
  get isAuthError(): boolean {
    return this.code === 'AUTH_REQUIRED';
  }

  /** First message for a field, for inline form errors. */
  fieldError(field: string): string | undefined {
    return this.fields?.[field]?.[0];
  }
}

/**
 * The CSRF token lives in memory only.
 *
 * Keeping it out of localStorage means a cross-site script cannot read it and
 * re-create the double-submit pair. It is re-fetched from /api/auth/session on
 * every page load, which requires the HttpOnly cookie.
 */
let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

/** Called when any request comes back 401, so the app can reset to signed-out. */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

export interface RequestOptions {
  /** Query parameters; undefined and null entries are dropped. */
  query?: Record<string, string | number | undefined | null>;
  signal?: AbortSignal;
  /** Suppresses the global 401 handler, e.g. for the initial session probe. */
  ignoreUnauthorized?: boolean;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const url = new URL(`${BASE_URL}${path}`);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }

  const headers = new Headers();
  const isFormData = body instanceof FormData;
  if (body !== undefined && !isFormData) headers.set('Content-Type', 'application/json');
  // Only state-changing verbs need it, and only when we have one.
  if (csrfToken && method !== 'GET') headers.set('X-CSRF-Token', csrfToken);

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      // Sends and accepts the HttpOnly session cookie.
      credentials: 'include',
      body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
      signal: options.signal ?? null,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError('NETWORK_ERROR', 'Could not reach the server. Check your connection.', 0);
  }

  let payload: ApiResponse<T>;
  try {
    payload = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError('INTERNAL_ERROR', `Unexpected response from the server (${response.status}).`, response.status);
  }

  if (payload.success) return payload.data;

  if (payload.error.code === 'AUTH_REQUIRED' && !options.ignoreUnauthorized) {
    onUnauthorized?.();
  }

  throw new ApiError(payload.error.code, payload.error.message, response.status, payload.error.fields);
}

export const api = {
  get: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('POST', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('PATCH', path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('PUT', path, body, options),
  delete: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>('DELETE', path, undefined, options),
};

export { BASE_URL as API_BASE_URL };
